import {
  BarsOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
  LockFilled,
  PicLeftOutlined,
} from '@ant-design/icons';
import { Button, Popover, Tooltip } from 'antd';
import React, { useEffect, useState } from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import styled from 'styled-components';

export type OrderColumn = {
  label: string;
  key: string;
  active: boolean;
};

const ReorderColumnsOrderV2 = (props: {
  columns: OrderColumn[];
  setColumns: (payload: OrderColumn[]) => void;
}) => {
  const [columns, setColumns] = useState<OrderColumn[]>([]);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (visible) {
      setColumns(props.columns);
    }
  }, [visible, props.columns]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Popover
      title={null}
      visible={visible}
      onVisibleChange={vis => setVisible(vis)}
      trigger={'click'}
      placement="rightTop"
      content={
        <Wrapper>
          <DragDropContext
            onDragEnd={(result, provided) => {
              if (!result.destination) {
                // dropped outside oof the box
                return;
              }

              const sourceIdx = result.source.index;
              const targetIdx = result.destination.index;
              const newColumns = [...columns];
              const [moved] = newColumns.splice(sourceIdx, 1);
              newColumns.splice(targetIdx, 0, moved);
              setColumns(newColumns);
            }}
          >
            <Droppable droppableId="submitted-ticket-list-column-setting">
              {(provided, snapshot) => (
                <div
                  className="column-container"
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                  style={{ width: 200 }}
                  // style={getListStyle(snapshot.isDraggingOver)}
                >
                  <div className={'column-li'}>
                    <LockFilled /> Actions
                  </div>
                  <div className={'column-li'}>
                    <LockFilled /> SAID
                  </div>
                  {columns.map((item, index) => (
                    <Draggable
                      key={item.key}
                      draggableId={`${item.key}-${index}`}
                      index={index}
                    >
                      {(provided, snapshot) => (
                        <div
                          className={
                            'column-li' +
                            (snapshot.isDragging ? ' dragging' : '')
                          }
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          // style={getItemStyle(
                          //   snapshot.isDragging,
                          //   provided.draggableProps.style
                          // )}
                        >
                          <BarsOutlined /> {item.label}{' '}
                          <Tooltip
                            title={item.active ? 'Hide Column' : 'Show Column'}
                          >
                            {item.active ? (
                              <EyeOutlined
                                onClick={() =>
                                  setColumns(cols =>
                                    cols.map(c => {
                                      if (c.key === item.key) {
                                        return { ...c, active: false };
                                      }

                                      return c;
                                    })
                                  )
                                }
                              />
                            ) : (
                              <EyeInvisibleOutlined
                                onClick={() =>
                                  setColumns(cols =>
                                    cols.map(c => {
                                      if (c.key === item.key) {
                                        return { ...c, active: true };
                                      }

                                      return c;
                                    })
                                  )
                                }
                              />
                            )}
                          </Tooltip>
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
          <div className="btn-container">
            <Button
              size="small"
              onClick={() => setColumns(defaultOrderColumns)}
            >
              Reset
            </Button>
            <Button
              size="small"
              type="primary"
              onClick={() => {
                setVisible(false);
                props.setColumns(columns);
              }}
            >
              Apply
            </Button>
          </div>
        </Wrapper>
      }
    >
      <Button icon={<PicLeftOutlined />}>Sort Column</Button>
    </Popover>
  );
};

const Wrapper = styled.div`
  .column-container {
    margin-top: -10px;

    .column-li {
      margin: 0 0 0 -10px;
      font-size: 12px;
      padding: 1px 5px;

      &.dragging {
        background: #d1d1d161;
      }

      .anticon {
        color: #59595999;
      }
    }
  }

  .btn-container {
    text-align: right;
    margin-right: -5px;

    .ant-btn {
      font-size: 12px;
      margin-left: 10px;
    }
  }
`;

export const defaultOrderColumns = [
  { label: 'Region Identifier', key: 'region_ref_number', active: true },
  { label: 'Project Codes', key: 'project_code', active: true },
  { label: 'PO Number', key: 'po_reference_number', active: true },
  { label: 'Purchase Request', key: 'purchase_request_number', active: true },
  { label: 'Region', key: 'region', active: true },
  {
    label: 'Assignee Region Identifier',
    key: 'assignee_region_identifier',
    active: true,
  },
  { label: 'Requester Name', key: 'requester_name', active: true },
  { label: 'Recipient Email', key: 'buyer_email', active: true },
  { label: 'Recipient Name', key: 'buyer_name', active: true },
  { label: 'Entity Name', key: 'entity_name', active: true },
  { label: 'Shipping Address', key: 'shipping_address', active: true },
  { label: 'Exchange Rate', key: 'exchange_rate', active: true },
  { label: 'Total', key: 'total_price_all', active: true },
  { label: 'Total Order', key: 'total_price', active: true },
  {
    label: 'Total Order (Fulfilled)',
    key: 'total_price_fulfilled',
    active: true,
  },
  {
    label: 'Total External Remittance (email reader)',
    key: 'total_external_remittance',
    active: true,
  },
  { label: 'Total Cost', key: 'total_cost', active: true },
  { label: 'Current Tax', key: 'current_tax', active: true },
  { label: 'All Qty', key: 'all_qty', active: true },
  { label: 'All Fulfilled Qty', key: 'all_fulfilled_qty', active: true },
  { label: 'Total Fulfilled Amount', key: 'all_fulfilled_amt', active: true },
  { label: 'Total Margin', key: 'total_margin', active: true },
  {
    label: 'Cumulative Margin (%)',
    key: 'cumulative_margin_percentage',
    active: true,
  },
  { label: 'Currency', key: 'currency', active: true },
  { label: 'Operation Status', key: 'operation_status', active: true },
  { label: 'Payment Status', key: 'payment_status', active: true },
  { label: 'Issue Date', key: 'issue_date', active: true },
  { label: 'Invoice Date', key: 'invoice_date', active: true },
  { label: 'DO Date', key: 'do_date', active: true },
  { label: 'Payment Date', key: 'payment_date', active: true },
  { label: 'Delivered Date', key: 'completed_at', active: true },
  { label: 'Reason', key: 'reason', active: true },
  { label: 'SAID Remarks', key: 'remarks', active: true },
  { label: 'Days to Fulfillment', key: 'days_to_fulfill', active: true },
  { label: 'Xero Sync', key: 'xero_sync', active: true },
  { label: 'Consolidated', key: 'is_consolidated_line_items', active: true },
  { label: 'NCPR', key: 'is_ncpr', active: true },
  { label: 'Created At', key: 'created_at', active: true },
  { label: 'Created By', key: 'created_by_user_name', active: true },
];

export default ReorderColumnsOrderV2;
